import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, Check, X } from 'lucide-react';
import { useReducedMotion } from 'framer-motion';
import { SCORED_QUESTIONS, TOTAL_QUESTIONS } from './scoring';
import { useScore } from './ScoreContext';
import { createScoreResultUrl } from './scoreResultUrl';
import { trackScoreComplete } from '../lib/analytics';
export function Diagnostic() {
  const {
    answers,
    setAnswer,
    answeredCount,
    result,
    isComplete,
    isDiagnosticOpen,
    closeDiagnostic
  } = useScore();
  const [currentIndex, setCurrentIndex] = useState(0);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const reduceMotion = useReducedMotion();

  // Sync to the first unanswered question only when the panel opens.
  // Do NOT re-sync when answers change — that races the post-answer
  // auto-advance and skips questions (commonly Q2).
  useEffect(() => {
    if (!isDiagnosticOpen) return;
    const index = SCORED_QUESTIONS.findIndex(question => answers[question.id] === undefined);
    setCurrentIndex(index === -1 ? TOTAL_QUESTIONS - 1 : index);
    // intentionally only when open state flips to true
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDiagnosticOpen]);
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (isDiagnosticOpen) {
      openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      if (!dialog.open) {
        dialog.showModal();
      }
      // Move focus into the dialog; native modal keeps Tab within it.
      window.requestAnimationFrame(() => {
        dialog.focus();
      });
    } else if (dialog.open) {
      dialog.close();
    }
  }, [isDiagnosticOpen]);
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog || !isDiagnosticOpen) return;
    const focusables = () => [...dialog.querySelectorAll<HTMLElement>('button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')].filter(el => !el.hasAttribute('disabled') && el.tabIndex !== -1);
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const list = focusables();
      if (list.length === 0) {
        e.preventDefault();
        dialog.focus();
        return;
      }
      const first = list[0];
      const last = list[list.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && (active === first || active === dialog)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    const onFocusIn = (e: FocusEvent) => {
      if (e.target instanceof Node && !dialog.contains(e.target)) {
        const list = focusables();
        (list[0] || dialog).focus();
      }
    };
    dialog.addEventListener('keydown', onKeyDown);
    document.addEventListener('focusin', onFocusIn);
    return () => {
      dialog.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('focusin', onFocusIn);
    };
  }, [isDiagnosticOpen]);
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const onClose = () => {
      closeDiagnostic();
      const opener = openerRef.current;
      openerRef.current = null;
      window.requestAnimationFrame(() => {
        opener?.focus?.();
      });
    };
    dialog.addEventListener('close', onClose);
    return () => dialog.removeEventListener('close', onClose);
  }, [closeDiagnostic]);
  const question = SCORED_QUESTIONS[currentIndex];
  const selectedScore = answers[question.id];
  const canGoBack = currentIndex > 0;
  const canGoForward = currentIndex < TOTAL_QUESTIONS - 1;
  const isLastQuestion = !canGoForward;
  const selectAnswer = (score: number) => {
    setAnswer(question.id, score);
    if (canGoForward) {
      const delay = reduceMotion ? 0 : 180;
      window.setTimeout(() => setCurrentIndex(index => index + 1), delay);
    }
  };
  const showScore = () => {
    if (!isComplete || !result) {
      const missingIndex = SCORED_QUESTIONS.findIndex(item => answers[item.id] === undefined);
      setCurrentIndex(missingIndex === -1 ? TOTAL_QUESTIONS - 1 : missingIndex);
      return;
    }
    trackScoreComplete(result.totalScore, result.band);
    // Same-tab navigation — window.open is often blocked and feels like a dead button.
    window.location.assign(createScoreResultUrl(result));
  };
  return <dialog ref={dialogRef} tabIndex={-1} aria-labelledby="diagnostic-title" className="fixed inset-0 z-[60] m-0 h-full max-h-none w-full max-w-none overflow-y-auto border-0 bg-paper p-0 open:flex open:flex-col focus:outline-none" data-id="element-109" data-mp="2d9q8sxcpli5j">
      <div className="mx-auto w-full max-w-3xl flex-1 px-6 py-6 md:py-10" data-id="element-110" data-mp="35y42j0wrlsba">
        <header className="flex items-center justify-between gap-4" data-id="element-111" data-mp="3aqx9zwjmber2">
          <div data-id="element-112" data-mp="zamxw0ryghtc">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-seaglass" data-id="element-113" data-mp="za4wkpqfrdek">
              Reader-to-Revenue Score
            </p>
            <p className="mt-1 text-sm font-medium text-text-muted" data-id="element-114" data-mp="ua92pjbgd2cq">
              {answeredCount} of {TOTAL_QUESTIONS} answered
            </p>
          </div>
          <button type="button" data-diagnostic-close onClick={() => dialogRef.current?.close()} className="rounded-full p-3 text-text-muted transition-colors hover:bg-paper-dark hover:text-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-seaglass" aria-label="Close diagnostic" data-id="element-115" data-mp="1436el6lxvt2d">
            <X className="h-5 w-5" aria-hidden="true" data-id="element-116" data-mp="pe1xjsqvruvu" />
          </button>
        </header>

        <div className="mt-6 flex gap-1.5" aria-hidden="true" data-id="element-117" data-mp="kcun1lxcshlh">
          {SCORED_QUESTIONS.map(item => <span key={item.id} className={`h-1.5 flex-1 rounded-full ${answers[item.id] === undefined ? 'bg-gray-200' : 'bg-seaglass'}`} data-id="element-118" data-mp="20rcoa7xpxbni" />)}
        </div>

        <div className="py-12 md:py-16" data-id="element-119" data-mp="1vvf7y1mgg5aq">
          <p className="text-sm font-medium text-text-muted" data-id="element-120" data-mp="tgfz7kcjk7yb">
            Question {currentIndex + 1} of {TOTAL_QUESTIONS}
          </p>
          <h1 id="diagnostic-title" className="mt-4 max-w-3xl font-serif text-3xl leading-tight text-navy md:text-5xl" data-id="element-121" data-mp="ycwowize2udi">
            {question.prompt}
          </h1>

          <div className="mt-9 flex flex-col gap-3" data-id="element-122" data-mp="17fo0cis9lq55">
            {question.options.map(option => {
            const isSelected = selectedScore === option.score;
            return <button key={option.letter} type="button" aria-pressed={isSelected} onClick={() => selectAnswer(option.score)} className={`flex min-h-[72px] w-full items-start gap-4 rounded-2xl border p-5 text-left transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-seaglass md:p-6 ${isSelected ? 'border-seaglass bg-seaglass/10 ring-1 ring-seaglass/40' : 'border-gray-200 bg-white hover:border-seaglass/50'}`} data-id="element-123" data-mp="eqh5puvxlkp6">
                  <span className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-sm font-semibold ${isSelected ? 'border-seaglass bg-seaglass text-white' : 'border-gray-300 text-navy'}`} data-id="element-124" data-mp="3s4llxrmufqaq">
                    {isSelected ? <Check className="h-4 w-4" aria-hidden="true" data-id="element-125" data-mp="3fkyftci6s42y" /> : option.letter}
                  </span>
                  <span className="leading-relaxed text-text" data-id="element-126" data-mp="3apspm1hr4392">{option.text}</span>
                </button>;
          })}
          </div>

          <div className="mt-10 flex items-center justify-between gap-4" data-id="element-127" data-mp="31nji7khs4pzw">
            <button type="button" onClick={() => setCurrentIndex(index => index - 1)} disabled={!canGoBack} className="inline-flex items-center gap-2 rounded-xl px-4 py-3 font-semibold text-navy transition-colors hover:bg-paper-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-seaglass disabled:cursor-not-allowed disabled:opacity-40" data-id="element-128" data-mp="2wwjjqat0hzva">
              <ArrowLeft className="h-4 w-4" aria-hidden="true" data-id="element-129" data-mp="2iw8ufc7cotc8" />
              Back
            </button>

            {isLastQuestion ? <div className="flex flex-col items-end gap-2" data-id="element-130" data-mp="2dzs54eof08xk">
                <button type="button" onClick={showScore} disabled={selectedScore === undefined || !isComplete} className="inline-flex min-h-[52px] items-center gap-2 rounded-xl bg-sand px-6 py-3 font-bold text-navy transition-colors hover:bg-sand-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-seaglass disabled:cursor-not-allowed disabled:opacity-40" data-id="element-131" data-mp="5qamgkgu8abr">
                  See my score
                  <ArrowRight className="h-4 w-4" aria-hidden="true" data-id="element-132" data-mp="3j33ywheb1867" />
                </button>
                {selectedScore !== undefined && !isComplete && <p className="max-w-xs text-right text-sm text-text-muted" data-id="element-133" data-mp="3nwfa0uq5gb3x">
                    One earlier question is still unanswered. Use Back to finish
                    it.
                  </p>}
              </div> : <button type="button" onClick={() => setCurrentIndex(index => index + 1)} disabled={selectedScore === undefined} className="inline-flex items-center gap-2 rounded-xl px-4 py-3 font-semibold text-navy transition-colors hover:bg-paper-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-seaglass disabled:cursor-not-allowed disabled:opacity-40" data-id="element-134" data-mp="3051i2pvwuhjh">
                Next question
                <ArrowRight className="h-4 w-4" aria-hidden="true" data-id="element-135" data-mp="2uir4npmg5nhn" />
              </button>}
          </div>
        </div>
      </div>
    </dialog>;
}